import PageBackTitle from "../../Component/PageBackTitle";
import React, { useState, useEffect, useRef } from "react";
import { MdOutlineModeEditOutline } from "react-icons/md";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { HiOutlinePlus } from "react-icons/hi";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { MdExpandMore, MdExpandLess } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../../Config.js";
import Pagination from "../../Component/Pagination.js";
import useClickOutside from "../../useClickOutside";
import "./customer.css";

// Import the AddCustomer and EditCustomer components
import AddCustomer from "./AddCustomer";
import EditCustomer from "./EditCustomer";

const CUSTOMER_TYPES = [
  "Defence",
  "Government",
  "Retail Outlet",
  "Private",
  // "Distributor",
  // "Retailer",
];

function Customer({ isSidebarExpanded }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const fetchTimeout = useRef(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  const [filterCustomerType, setFilterCustomerType] = useState(""); // filter dropdown

  useClickOutside(
    [containerRef, buttonRef],
    () => {
      setIsSortPopup(false);
      setSelectedRowIndex(null);
    },
    200
  );
  useClickOutside(
    [popupRef, buttonRef],
    () => {
      setIsSortPopup(false);
      setSelectedRowIndex(null);
    },
    300
  );

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const fetchData = async (
    currentPage = page,
    perPage = itemsPerPage,
    search = searchInput,
    customerType = filterCustomerType
  ) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const params = {
        page: currentPage,
        per_page: perPage,
      };
      if (search && search.trim() !== "") params.search = search.trim();
      if (customerType) params.customer_type = customerType;

      const response = await axios.get(`${API_URL}/api/barrel/customers`, {
        headers: {
          Authorization: authKey,
        },
        params,
      });

      // API returns { customers, totalItems, currentPage, itemsPerPage, totalPages }
      const data = response.data;
      setCustomers(Array.isArray(data.customers) ? data.customers : []);
      setTotalItems(data.totalItems ?? 0);
    } catch (error) {
      console.error("Error fetching customers:", error);
      showSnackbar("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search + fetch when page/itemsPerPage/filter/search change
  useEffect(() => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      fetchData(page, itemsPerPage, searchInput, filterCustomerType);
    }, 400);
    return () => clearTimeout(fetchTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage, filterCustomerType, searchInput]);

  const handlePaginationChange = (currentPage, perPage) => {
    setPage(currentPage);
    setItemsPerPage(perPage);
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("itemsPerPage", perPage);
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
    setPage(1); // reset to first page on search
  };

  const handleRefresh = () => {
    setSearchInput("");
    setFilterCustomerType("");
    fetchData(1, itemsPerPage, "", "");
  };

  const handleButtonClick = (index, event) => {
    event.stopPropagation();
    setSelectedRowIndex(index === selectedRowIndex ? null : index);
  };

  const togglePopup = (customerID) => {
    navigate(`/customer-view/${customerID}`);
  };

  const handleOpenAddCustomer = () => {
    setIsAddCustomerOpen(true);
  };

  const handleCloseAddCustomer = () => {
    setIsAddCustomerOpen(false);
    fetchData(page, itemsPerPage, searchInput, filterCustomerType);
  };

  const handleOpenEditCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    setIsEditCustomerOpen(true);
    setSelectedRowIndex(null);
  };

  const handleCloseEditCustomer = () => {
    setIsEditCustomerOpen(false);
    setSelectedCustomerId(null);
    fetchData(page, itemsPerPage, searchInput, filterCustomerType);
  };

  const handleViewClick = async (customer_id) => {
    navigate(`/barrel-view-customer/${customer_id}`);
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div
          className="data-list-content"
          ref={containerRef}
          style={{ position: "relative" }}
        >
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>All Customers</PageBackTitle>

              <div className="data-list-heading-content-right-side">
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {/* Filter dropdown for customer type */}
                  <select
                    value={filterCustomerType}
                    onChange={(e) => {
                      setFilterCustomerType(e.target.value);
                      setPage(1);
                    }}
                    className="filter-dropdown"
                    // className="newdashboard-select"
                  >
                    <option value="">All Types</option>
                    {CUSTOMER_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>

                  <div className="search-bar-container">
                    <input
                      type="text"
                      placeholder="Search by Customers or SAP..."
                      value={searchInput}
                      onChange={handleSearchInputChange}
                      className="search-bar"
                    />
                    <IoSearchOutline className="search-bar-icon" />
                  </div>

                  <Tooltip title="New Customer" arrow>
                    <button
                      className="new-button"
                      onClick={handleOpenAddCustomer}
                    >
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>

                  <Tooltip title="Refresh List" arrow>
                    <button className="refresh-button" onClick={handleRefresh}>
                      <TbRefresh className="refresh-icon" />
                    </button>
                  </Tooltip>

                  <button
                    className="sort-button"
                    onClick={() => setIsSortPopup((s) => !s)}
                    ref={buttonRef}
                  >
                    <PiDotsThreeOutlineVerticalFill className="sort-icon" />
                  </button>
                </div>

                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-content">
                      <div className="sort-by">SORT BY</div>
                      <div className="sort-by-button">
                        <button
                          onClick={() => {
                            const sorted = [...customers].sort((a, b) =>
                              (a.sap_code || "").localeCompare(b.sap_code || "")
                            );
                            setCustomers(sorted);
                            setIsSortPopup(false);
                          }}
                        >
                          SAP Code
                        </button>
                        <br />
                        <button
                          onClick={() => {
                            const sorted = [...customers].sort((a, b) =>
                              (a.customer_name || "").localeCompare(
                                b.customer_name || ""
                              )
                            );
                            setCustomers(sorted);
                            setIsSortPopup(false);
                          }}
                        >
                          Customer Name
                        </button>
                        <br />
                        <button
                          onClick={() => {
                            const sorted = [...customers].sort((a, b) =>
                              (a.customer_type || "").localeCompare(
                                b.customer_type || ""
                              )
                            );
                            setCustomers(sorted);
                            setIsSortPopup(false);
                          }}
                        >
                          Customer Type
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="data-list-body"
            onClick={() => {
              setIsSortPopup(false);
              setSelectedRowIndex(null);
            }}
            style={{ height: "86.6vh" }}
          >
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : customers.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>SAP CODE</th>
                        <th>CUSTOMER NAME</th>
                        <th>CUSTOMER TYPE</th>
                        <th>CONTACT</th>
                        <th>EMAIL</th>
                        {/* <th>BILLING CITY</th> */}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {customers.map((customer, index) => (
                        <tr key={customer.id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          <td
                            className="view-data"
                            // onClick={() => togglePopup(customer.id)}
                          >
                            {customer.sap_code || "NA"}
                          </td>
                          <td>{customer.customer_name || "NA"}</td>
                          <td>{customer.customer_type || "NA"}</td>
                          <td>{customer.contact_number || "NA"}</td>
                          <td>{customer.email || "NA"}</td>
                          {/* <td>{customer.billing_city || "NA"}</td> */}
                          <td className="edit-pop">
                            <button
                              className="edit-data-button"
                              onClick={(event) =>
                                handleButtonClick(index, event)
                              }
                            >
                              <div className="edit-button">
                                <MdExpandMore />
                              </div>
                            </button>
                          </td>

                          {/* <button
                            onClick={() => handleViewClick(customer.id)}
                            className="button-bg"
                          >
                            View
                          </button> */}

                          {selectedRowIndex === index && (
                            <div className="edit-popup" ref={popupRef}>
                              <div className="edit-popup-contant">
                                <div className="edit-popup-button">
                                  <button
                                    className="edit-button-inquiry"
                                    onClick={() =>
                                      handleOpenEditCustomer(customer.id)
                                    }
                                  >
                                    <MdOutlineModeEditOutline /> Edit
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Customer Found</div>
                  </div>
                )}
              </div>

              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />

              {isAddCustomerOpen && (
                <AddCustomer
                  onClose={handleCloseAddCustomer}
                  onSuccess={handleCloseAddCustomer}
                  showSnackbar={showSnackbar}
                />
              )}

              {isEditCustomerOpen && (
                <EditCustomer
                  onClose={handleCloseEditCustomer}
                  onSuccess={handleCloseEditCustomer}
                  showSnackbar={showSnackbar}
                  customerId={selectedCustomerId}
                />
              )}
            </div>
          </div>
        </div>

        {/* {isAddCustomerOpen && (
                <AddCustomer
                  onClose={handleCloseAddCustomer}
                  onSuccess={handleCloseAddCustomer}
                  showSnackbar={showSnackbar}
                />
              )}

              {isEditCustomerOpen && (
                <EditCustomer
                  onClose={handleCloseEditCustomer}
                  onSuccess={handleCloseEditCustomer}
                  showSnackbar={showSnackbar}
                  customerId={selectedCustomerId}
                />
              )} */}
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default Customer;

// import React, { useState, useEffect, useRef } from "react";
// import { Link } from "react-router-dom";
// import { MdOutlineModeEditOutline } from "react-icons/md";
// import Snackbar from "@mui/material/Snackbar";
// import MuiAlert from "@mui/material/Alert";
// import { HiOutlinePlus } from "react-icons/hi";
// import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import { IoSearchOutline } from "react-icons/io5";
// import { MdExpandMore, MdExpandLess } from "react-icons/md";
// import { TbRefresh } from "react-icons/tb";
// import Tooltip from "@mui/material/Tooltip";
// import API_URL from "../../Config.js";
// import Pagination from "../../Component/Pagination.js";
// import useClickOutside from '../../useClickOutside';

// // Import your AddCustomer and EditCustomer components
// import AddCustomer from "./AddCustomer";
// import EditCustomer from "./EditCustomer";

// function Customer({ isSidebarExpanded, customerID }) {
//   const [customers, setCustomers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
//   const [isSortPopup, setIsSortPopup] = useState(false);
//   const [selectedRowIndex, setSelectedRowIndex] = useState(null);
//   const [searchInput, setSearchInput] = useState("");
//   const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
//   const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
//   const [selectedCustomerId, setSelectedCustomerId] = useState(null);
//   const navigate = useNavigate();
//   const [page, setPage] = useState(0);
//   const [itemsPerPage, setItemsPerPage] = useState(50);
//   const [totalItems, setTotalItems] = useState(0);
//   const fetchTimeout = useRef(null);
//   const containerRef = useRef(null);
//   const buttonRef = useRef(null);
//   const popupRef = useRef(null);
//   const [sortOrder, setSortOrder] = useState("asc");
//   const [sortColumn, setSortColumn] = useState(null);

//   const handleButtonClick = (index, event) => {
//     event.stopPropagation();
//     setSelectedRowIndex(index === selectedRowIndex ? null : index);
//   };

//   const handleClosePopup = () => {
//     setIsSortPopup(false);
//     setSelectedRowIndex(null);
//   };

//   useClickOutside([containerRef, buttonRef], handleClosePopup, 200);
//   useClickOutside([popupRef, buttonRef], handleClosePopup, 300);

//   const handleSort = (column) => {
//     const order =
//       sortColumn === column && sortOrder[column] === "asc" ? "desc" : "asc";

//     setSortColumn(column);
//     setSortOrder((prevOrder) => ({
//       ...prevOrder,
//       [column]: order,
//     }));

//     setIsSortPopup(false);

//     const sortedCustomers = [...customers].sort((a, b) => {
//       const aValue = a[column]?.toString().toLowerCase() || "";
//       const bValue = b[column]?.toString().toLowerCase() || "";

//       if (aValue < bValue) return order === "asc" ? -1 : 1;
//       if (aValue > bValue) return order === "asc" ? 1 : -1;
//       return 0;
//     });

//     setCustomers(sortedCustomers);
//   };

//   const fetchData = async (currentPage, itemsPerPage) => {
//     try {
//       setLoading(true);
//       const authKey = localStorage.getItem("authKey");

//       const response = await axios.get(`${API_URL}/api/get_customers`, {
//         headers: {
//           Authorization: authKey,
//         },
//         params: {
//           page: currentPage,
//           per_page: itemsPerPage,
//         },
//       });

//       setCustomers(response.data.Customers);
//       setTotalItems(response.data.TotalItems);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching customers data:", error.message);
//       setLoading(false);
//     }
//   };

//   const handleSnackbarClose = (event, reason) => {
//     if (reason === "clickaway") {
//       return;
//     }
//     setSnackbarOpen(false);
//   };

//   const showSnackbar = (message) => {
//     setSnackbarMessage(message);
//     setSnackbarOpen(true);
//   };

//   const handleSortpopup = () => {
//     setIsSortPopup(!isSortPopup);
//   };

//   const handleSearch = async (currentPage, itemsPerPage) => {
//     if (!searchInput.trim()) {
//       fetchData(currentPage, itemsPerPage);
//       return;
//     }

//     try {
//       setLoading(true);
//       const authKey = localStorage.getItem("authKey");

//       const response = await axios.get(`${API_URL}/api/search_customers`, {
//         headers: {
//           Authorization: authKey,
//           "Content-Type": "application/json",
//         },
//         params: {
//           query: searchInput,
//           page: currentPage,
//           per_page: itemsPerPage,
//         },
//       });

//       if (response.status === 200) {
//         setCustomers(response.data.Customers);
//         setTotalItems(response.data.TotalItems);
//       } else {
//         console.error("Error fetching search results:", response.statusText);
//       }
//     } catch (error) {
//       console.error("Error fetching search results:", error.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     setLoading(true);
//     clearTimeout(fetchTimeout.current);

//     fetchTimeout.current = setTimeout(() => {
//       if (searchInput.trim() === "") {
//         fetchData(page, itemsPerPage);
//       } else {
//         handleSearch(page, itemsPerPage);
//       }
//     }, 600);

//     return () => clearTimeout(fetchTimeout.current);
//   }, [searchInput, page, itemsPerPage]);

//   const handlePaginationChange = (currentPage, itemsPerPage) => {
//     setPage(currentPage);
//     setItemsPerPage(itemsPerPage);
//     localStorage.setItem("currentPage", currentPage);
//     localStorage.setItem("itemsPerPage", itemsPerPage);
//   };

//   const handleSearchInputChange = (event) => {
//     setSearchInput(event.target.value);
//   };

//   const handleRefresh = () => {
//     setSearchInput("");
//     fetchData(page, itemsPerPage);
//   };

//   const closePopups = () => {
//     setIsSortPopup(false);
//     setSelectedRowIndex(null);
//   };

//   const togglePopup = (customerID) => {
//     navigate(`/customer-view/${customerID}`);
//   };

//   const handleOpenAddCustomer = () => {
//     setIsAddCustomerOpen(true);
//   };

//   const handleCloseAddCustomer = () => {
//     setIsAddCustomerOpen(false);
//     fetchData(page, itemsPerPage); // Refresh data after closing
//   };

//   const handleOpenEditCustomer = (customerId) => {
//     setSelectedCustomerId(customerId);
//     setIsEditCustomerOpen(true);
//     setSelectedRowIndex(null); // Close the edit dropdown
//   };

//   const handleCloseEditCustomer = () => {
//     setIsEditCustomerOpen(false);
//     setSelectedCustomerId(null);
//     fetchData(page, itemsPerPage); // Refresh data after closing
//   };

//   return (
//     <>
//       <div
//         className={`data-list-content-container page-content ${
//           isSidebarExpanded ? "expanded" : "collapsed"
//         }`}
//       >
//         <div className="data-list-content" ref={containerRef}>
//           <div className="data-list-header">
//             <div className="data-list-header-content">
//               <div className="data-list-heading-content-left-side">
//                 All Customers
//               </div>
//               <div className="data-list-heading-content-right-side">
//                 <div className="search-bar-container">
//                   <input
//                     type="text"
//                     placeholder="Search Customers..."
//                     value={searchInput}
//                     onChange={handleSearchInputChange}
//                     className="search-bar"
//                   />
//                   <IoSearchOutline className="search-bar-icon" />
//                 </div>

//                 <Tooltip title="New Customer" arrow>
//                   <button className="new-button" onClick={handleOpenAddCustomer}>
//                     <HiOutlinePlus className="new-plus-icon" /> New
//                   </button>
//                 </Tooltip>

//                 <Tooltip title="Refresh List" arrow>
//                   <button className="refresh-button" onClick={handleRefresh}>
//                     <TbRefresh className="refresh-icon" />
//                   </button>
//                 </Tooltip>

//                 <button
//                   className="sort-button"
//                   onClick={handleSortpopup}
//                   ref={buttonRef}
//                 >
//                   <PiDotsThreeOutlineVerticalFill className="sort-icon" />
//                 </button>

//                 {isSortPopup && (
//                   <div className="sort-popup-container" ref={containerRef}>
//                     <div className="sort-popup-content">
//                       <div className="sort-by">
//                         SORT BY{" "}
//                         {sortOrder[sortColumn] === "asc"
//                           ? "(Ascending)"
//                           : "(Descending)"}
//                       </div>
//                       <div className="sort-by-button">
//                         <button onClick={() => handleSort("sold_to_party")}>
//                           Sold to Party{" "}
//                           {sortOrder.sold_to_party === "asc" ? (
//                             <MdExpandMore />
//                           ) : (
//                             <MdExpandLess />
//                           )}
//                         </button>
//                         <br />
//                         <button onClick={() => handleSort("sap_code")}>
//                           SAP Code{" "}
//                           {sortOrder.sap_code === "asc" ? (
//                             <MdExpandMore />
//                           ) : (
//                             <MdExpandLess />
//                           )}
//                         </button>
//                         <br />
//                         <button onClick={() => handleSort("customer_name")}>
//                           Customer Name{" "}
//                           {sortOrder.customer_name === "asc" ? (
//                             <MdExpandMore />
//                           ) : (
//                             <MdExpandLess />
//                           )}
//                         </button>
//                         <br />
//                       </div>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           <div className="data-list-body" onClick={closePopups}>
//             <div className="data-list-scroll-body">
//               <div className="data-list-table">
//                 {loading ? (
//                   <div className="loader-container">
//                     <div className="loader"></div>
//                     <div>Loading...</div>
//                   </div>
//                 ) : customers.length > 0 ? (
//                   <table className="table multilevel-data">
//                     <thead>
//                       <tr>
//                         <th className="center-alignment">SR. NO.</th>
//                         <th onClick={() => handleSort("sold_to_party")}>
//                           SOLD TO PARTY{" "}
//                           {sortConfig.column === "sold_to_party" ? (
//                             sortConfig.direction === "asc" ? (
//                               <MdExpandLess />
//                             ) : (
//                               <MdExpandMore />
//                             )
//                           ) : null}
//                         </th>
//                         <th onClick={() => handleSort("sap_code")}>
//                           SAP CODE{" "}
//                           {sortConfig.column === "sap_code" ? (
//                             sortConfig.direction === "asc" ? (
//                               <MdExpandLess />
//                             ) : (
//                               <MdExpandMore />
//                             )
//                           ) : null}
//                         </th>
//                         <th onClick={() => handleSort("customer_name")}>
//                           CUSTOMER NAME{" "}
//                           {sortConfig.column === "customer_name" ? (
//                             sortConfig.direction === "asc" ? (
//                               <MdExpandLess />
//                             ) : (
//                               <MdExpandMore />
//                             )
//                           ) : null}
//                         </th>
//                         <th></th>
//                       </tr>
//                     </thead>
//                     <tbody className="data-list-scroll-bodys">
//                       {customers.map((customer, index) => (
//                         <tr key={customer.id} className="hoverable-row">
//                           <td className="center-alignment">
//                             {customer.serial_no}
//                           </td>
//                           <td
//                             className="view-data"
//                             onClick={() => togglePopup(customer.id)}
//                           >
//                             {customer.sold_to_party || "NA"}
//                           </td>
//                           <td className="center-alignment">
//                             {customer.sap_code || "NA"}
//                           </td>
//                           <td>{customer.customer_name || "NA"}</td>
//                           <td className="edit-pop">
//                             <button
//                               className="edit-data-button"
//                               onClick={(event) => handleButtonClick(index, event)}
//                             >
//                               <div className="edit-button">
//                                 <MdExpandMore />
//                               </div>
//                             </button>
//                           </td>
//                           {selectedRowIndex === index && (
//                             <div className="edit-popup" ref={popupRef}>
//                               <div className="edit-popup-contant">
//                                 <div className="edit-popup-button">
//                                   <button
//                                     className="edit-button-inquiry"
//                                     onClick={() => handleOpenEditCustomer(customer.id)}
//                                   >
//                                     <MdOutlineModeEditOutline /> Edit
//                                   </button>
//                                 </div>
//                               </div>
//                             </div>
//                           )}
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 ) : (
//                   <div className="no-users-container">
//                     <div className="no-users-message">No Customer Found</div>
//                   </div>
//                 )}
//               </div>
//               <Pagination
//                 showPerPage={itemsPerPage}
//                 total={totalItems}
//                 onPaginationChange={handlePaginationChange}
//               />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Add Customer Popup */}
//       {isAddCustomerOpen && (
//         <AddCustomer
//           onClose={handleCloseAddCustomer}
//           onSuccess={handleCloseAddCustomer}
//           showSnackbar={showSnackbar}
//         />
//       )}

//       {/* Edit Customer Popup */}
//       {isEditCustomerOpen && (
//         <EditCustomer
//           onClose={handleCloseEditCustomer}
//           onSuccess={handleCloseEditCustomer}
//           showSnackbar={showSnackbar}
//           customerId={selectedCustomerId}
//         />
//       )}

//       <Snackbar
//         open={snackbarOpen}
//         autoHideDuration={6000}
//         onClose={handleSnackbarClose}
//         anchorOrigin={{ vertical: "top", horizontal: "right" }}
//         style={{ marginTop: "35px", marginLeft: "20px" }}
//       >
//         <MuiAlert onClose={handleSnackbarClose} severity="success">
//           {snackbarMessage}
//         </MuiAlert>
//       </Snackbar>
//     </>
//   );
// }

// export default Customer;
